/* eslint-disable tree-shaking/no-side-effects-in-initialization */
import { produce } from "immer";
import { z } from "zod";

import { assertNever } from "../errors.js";
import { MapLayerId } from "../idTypeBrands.js";
import { getNormalEnum } from "../pseudoEnums.js";

// === THEMES ===

export const MapThemeHex = z.literal("hex");
export const MapThemeDark = z.literal("dark");
export const MapThemeLight = z.literal("light");
export const MapThemeSatellite = z.literal("satellite");
export const MapThemeStreets = z.literal("streets");
export const MapThemeOutdoors = z.literal("outdoors");

const MapThemeLiteral = z.union([
  MapThemeHex,
  MapThemeDark,
  MapThemeLight,
  MapThemeSatellite,
  MapThemeStreets,
  MapThemeOutdoors,
]);
export type MapTheme = z.infer<typeof MapThemeLiteral>;
export const MapTheme = getNormalEnum(MapThemeLiteral);

// === VIEW ===

export const MapInitialView = z.object({
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  zoom: z.number().optional(),
  bearing: z.number().optional(),
  pitch: z.number().optional(),
  altitude: z.number().optional(),
  maxPitch: z.number().optional(),
  maxZoom: z.number().optional(),
  minPitch: z.number().optional(),
  minZoom: z.number().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  normalize: z.boolean().optional(),
  position: z.array(z.number()).optional(),
});
export type MapInitialView = z.infer<typeof MapInitialView>;

// === LAYER TYPES ===

export const MapLayerTypeScatterplot = z.literal("scatter");
export type MapLayerTypeScatterplot = z.infer<typeof MapLayerTypeScatterplot>;

export const MapLayerTypeText = z.literal("text");
export type MapLayerTypeText = z.infer<typeof MapLayerTypeText>;

export const MapLayerTypeArea = z.literal("area");
export type MapLayerTypeArea = z.infer<typeof MapLayerTypeArea>;

export const MapLayerTypeHeatmap = z.literal("heatmap");
export type MapLayerTypeHeatmap = z.infer<typeof MapLayerTypeHeatmap>;

export const MapLayerTypeDataset = z.literal("dataset");
export type MapLayerTypeDataset = z.infer<typeof MapLayerTypeDataset>;

export const MapLayerTypeLiteral = z.union([
  MapLayerTypeScatterplot,
  MapLayerTypeText,
  MapLayerTypeArea,
  MapLayerTypeHeatmap,
  MapLayerTypeDataset,
]);
export type MapLayerType = z.infer<typeof MapLayerTypeLiteral>;
export const MapLayerType = getNormalEnum(MapLayerTypeLiteral);

// === COORDINATES ===

// = LAT LNG =

export const MapCoordinateSystemLatLng = z.literal("latlng");
export type MapCoordinateSystemLatLng = z.infer<
  typeof MapCoordinateSystemLatLng
>;

export const MapCoordinateSystemLngLat = z.literal("lnglat");
export type MapCoordinateSystemLngLat = z.infer<
  typeof MapCoordinateSystemLngLat
>;

export const MapCoordinateSystemLatLngSeparate = z.literal("latlng_separate");
export type MapCoordinateSystemLatLngSeparate = z.infer<
  typeof MapCoordinateSystemLatLngSeparate
>;

export const MapCoordinateSystemLiteral = z.union([
  MapCoordinateSystemLatLng,
  MapCoordinateSystemLngLat,
  MapCoordinateSystemLatLngSeparate,
]);
export type MapCoordinateSystem = z.infer<typeof MapCoordinateSystemLiteral>;
export const MapCoordinateSystem = getNormalEnum(MapCoordinateSystemLiteral);

// === DATA ===

// = LAT LNG =

export const MapCoordinateDataLatLng = z.object({
  system: MapCoordinateSystemLatLng,
  dataFrameColumn: z.string().optional(),
});
export type MapCoordinateDataLatLng = z.infer<typeof MapCoordinateDataLatLng>;

export const MapCoordinateDataLngLat = z.object({
  system: MapCoordinateSystemLngLat,
  dataFrameColumn: z.string().optional(),
});
export type MapCoordinateDataLngLat = z.infer<typeof MapCoordinateDataLngLat>;

export const MapCoordinateDataLatLngSeparate = z.object({
  system: MapCoordinateSystemLatLngSeparate,
  dataFrameLatColumn: z.string().optional(),
  dataFrameLngColumn: z.string().optional(),
});
export type MapCoordinateDataLatLngSeparate = z.infer<
  typeof MapCoordinateDataLatLngSeparate
>;

export const MapCoordinateData = z.union([
  MapCoordinateDataLatLng,
  MapCoordinateDataLngLat,
  MapCoordinateDataLatLngSeparate,
]);
export type MapCoordinateData = z.infer<typeof MapCoordinateData>;

export const MapDataCoordinate = z.object({
  dataFrameName: z.string().optional(),
  coordinates: MapCoordinateData,
});
export type MapDataCoordinate = z.infer<typeof MapDataCoordinate>;

// = AREA =

const MapAreaDataSystemCustom = z.literal("custom");
type MapAreaDataSystemCustom = z.infer<typeof MapAreaDataSystemCustom>;
const MapAreaDataSystemDataset = z.literal("dataset");
type MapAreaDataSystemDataset = z.infer<typeof MapAreaDataSystemDataset>;
const MapAreaDataSystemLiteral = z.union([
  MapAreaDataSystemCustom,
  MapAreaDataSystemDataset,
]);
export const MapAreaDataSystem = getNormalEnum(MapAreaDataSystemLiteral);
export type MapAreaDataSystem = z.infer<typeof MapAreaDataSystemLiteral>;

// render "custom" areas, defined by selected data frame
export const MapDataAreaCustom = z.object({
  system: MapAreaDataSystemCustom.optional(), // optional for backwards compat
  dataFrameName: z.string().optional(),
  dataFrameColumn: z.string().optional(),
});
// render data areas by joining existing dataset (e.g. counties) and selected data frame
export const MapDataAreaDataset = z.object({
  system: MapAreaDataSystemDataset,
  dataFrameName: z.string().optional(),
  dataFrameColumn: z.string().optional(),
  datasetName: z.string().optional(),
  datasetField: z.string().optional(),
});
export const MapDataArea = z.union([MapDataAreaCustom, MapDataAreaDataset]);
export type MapDataArea = z.infer<typeof MapDataArea>;

// = DATASET =
export const MapDataDataset = z.object({
  // only the dataset has as a type field currently as previous data
  // types were built (and have persisted data) before we knew that
  // we would have to support other data types...
  type: z.literal("dataset"),
  datasetName: z.string().optional(),
});
export type MapDataDataset = z.infer<typeof MapDataDataset>;

// === COLOR ===

export const MapLayerColor = z.tuple([z.number(), z.number(), z.number()]);
export type MapLayerColor = z.infer<typeof MapLayerColor>;

export type MapLayerRGBA = [...MapLayerColor, number];

// === SIZE ===

export const MapLayerSizeTypeStatic = z.literal("static");
export type MapLayerSizeTypeStatic = z.infer<typeof MapLayerSizeTypeStatic>;

export const MapLayerSizeTypeDynamic = z.literal("dynamic");
export type MapLayerSizeTypeDynamic = z.infer<typeof MapLayerSizeTypeDynamic>;

export const MapLayerSizeTypeLiteral = z.union([
  MapLayerSizeTypeDynamic,
  MapLayerSizeTypeStatic,
]);
export const MapLayerSizeType = getNormalEnum(MapLayerSizeTypeLiteral);
export type MapLayerSizeType = z.infer<typeof MapLayerSizeTypeLiteral>;

export const MapLayerStaticSize = z.object({
  type: MapLayerSizeTypeStatic,
  value: z.number(),
});
export type MapLayerStaticSize = z.infer<typeof MapLayerStaticSize>;

export const MapLayerDynamicSize = z.object({
  type: MapLayerSizeTypeDynamic,
  dataFrameColumn: z.string().optional(),
  min: z.number(),
  max: z.number(),
});

export const MapLayerSize = z.union([MapLayerStaticSize, MapLayerDynamicSize]);
export type MapLayerSize = z.infer<typeof MapLayerSize>;

// === FILL ===

export const MapLayerFillTypeStatic = z.literal("static");
export type MapLayerFillTypeStatic = z.infer<typeof MapLayerSizeTypeStatic>;

export const MapLayerFillTypeDynamic = z.literal("dynamic");
export type MapLayerFillTypeDynamic = z.infer<typeof MapLayerSizeTypeDynamic>;

export const MapLayerFillTypeLiteral = z.union([
  MapLayerFillTypeStatic,
  MapLayerFillTypeDynamic,
]);
export type MapLayerFillType = z.infer<typeof MapLayerFillTypeLiteral>;
export const MapLayerFillType = getNormalEnum(MapLayerFillTypeLiteral);

const MapLayerBaseFill = z.object({
  type: MapLayerFillTypeLiteral,
  opacity: z.number().optional(), // optional to support backcompat
});

export const MapLayerStaticFill = MapLayerBaseFill.extend({
  type: MapLayerFillTypeStatic,
  color: MapLayerColor.optional(),
});
export type MapLayerStaticFill = z.infer<typeof MapLayerStaticFill>;

export const MapLayerDynamicFill = MapLayerBaseFill.extend({
  type: MapLayerFillTypeDynamic,
  dataFrameColumn: z.string().optional(),
  colors: z.array(MapLayerColor),
});
export type MapLayerDynamicFill = z.infer<typeof MapLayerDynamicFill>;

export const MapLayerFill = z.union([MapLayerDynamicFill, MapLayerStaticFill]);
export type MapLayerFill = z.infer<typeof MapLayerFill>;

// === OUTLINE ===

export const MapLayerOutline = z.object({
  color: MapLayerColor.optional(),
  width: z.number().optional(),
});
export type MapLayerOutline = z.infer<typeof MapLayerOutline>;

// === TEXT ===

export const MapLayerTextAnchorLiteral = z.enum(["start", "middle", "end"]);
export type MapLayerTextAnchor = z.infer<typeof MapLayerTextAnchorLiteral>;
export const MapLayerTextAnchor = getNormalEnum(MapLayerTextAnchorLiteral);

export const MapLayerTextAlignmentLiteral = z.enum(["top", "center", "bottom"]);
export type MapLayerTextAlignment = z.infer<
  typeof MapLayerTextAlignmentLiteral
>;
export const MapLayerTextAlignment = getNormalEnum(
  MapLayerTextAlignmentLiteral,
);

export const MapLayerText = z.object({
  dataFrameColumn: z.string().optional(),
  size: z.number(),
  color: MapLayerColor,
  anchor: MapLayerTextAnchorLiteral,
  alignment: MapLayerTextAlignmentLiteral,
  backgroundColor: MapLayerColor.optional(),
});
export type MapLayerText = z.infer<typeof MapLayerText>;

// === AGGREGATION ===

export const MapAggregationTypeLiteral = z.enum(["SUM", "MEAN"]);
export type MapAggregationType = z.infer<typeof MapAggregationTypeLiteral>;
export const MapAggregationType = getNormalEnum(MapAggregationTypeLiteral);

// === DATASET JOIN ===
export const MapDatasetJoin = z.object({
  dataFrameName: z.string().optional(),
  dataFrameColumn: z.string().optional(),
  datasetField: z.string().optional(),
});

// === LAYERS ===

const MapBaseLayer = z.object({
  type: MapLayerTypeLiteral,
  id: MapLayerId,
  name: z.string().optional(),
  // interactions
  /** @deprecated, unused */
  autoHighlight: z.boolean().optional(),
  tooltipDataFrameColumns: z.array(z.string()),
  // styles
  visible: z.boolean(), // defaults to true
  opacity: z.number(),
  highlightColor: MapLayerColor.optional(),
});

export const MapScatterplotLayer = MapBaseLayer.extend({
  type: MapLayerTypeScatterplot,
  data: MapDataCoordinate,
  // styles
  fill: MapLayerFill.optional(),
  outline: MapLayerOutline.optional(),
  radius: MapLayerSize,
});
export type MapScatterplotLayer = z.infer<typeof MapScatterplotLayer>;

export const MapTextLayer = MapBaseLayer.extend({
  type: MapLayerTypeText,
  data: MapDataCoordinate,
  text: MapLayerText,
});
export type MapTextLayer = z.infer<typeof MapTextLayer>;

export const MapAreaLayer = MapBaseLayer.extend({
  type: MapLayerTypeArea,
  data: MapDataArea,
  fill: MapLayerFill.optional(),
  outline: MapLayerOutline.optional(),
  pointRadius: MapLayerSize,
  text: MapLayerText.optional(),
});
export type MapAreaLayer = z.infer<typeof MapAreaLayer>;

export const MapHeatmapLayer = MapBaseLayer.extend({
  type: MapLayerTypeHeatmap,
  data: MapDataCoordinate,
  aggregationType: MapAggregationTypeLiteral,
  size: z.number(),
  fill: MapLayerDynamicFill,
});
export type MapHeatmapLayer = z.infer<typeof MapHeatmapLayer>;

export const MapDatasetLayer = MapBaseLayer.extend({
  type: MapLayerTypeDataset,
  data: MapDataDataset,
  fill: MapLayerFill.optional(),
  outline: MapLayerOutline.optional(),
  join: MapDatasetJoin.optional(),
});
export type MapDatasetLayer = z.infer<typeof MapDatasetLayer>;

export const MapLayer = z.union([
  MapScatterplotLayer,
  MapTextLayer,
  MapAreaLayer,
  MapHeatmapLayer,
  MapDatasetLayer,
]);
export type MapLayer = z.infer<typeof MapLayer>;

// === MAP ===

export const Map = z.object({
  layers: z.array(MapLayer),
  initialView: MapInitialView.optional(),
  theme: MapThemeLiteral,
  isLegendOpen: z.boolean().optional(),
});
export type Map = z.infer<typeof Map>;

/** Returns a new Map cell config that is cleaned up for Hex yml export. */
export function cleanMapForExport(map: Map): Map {
  return produce(map, (draft) => {
    for (const layer of draft.layers) {
      if ("coordinates" in layer.data) {
        layer.data.coordinates = cleanCoordinateDataForExport(
          layer.data.coordinates,
        );
      }
    }
  });
}

// When switching between coordinate systems,
// parameters from the previously selected system seem to sometimes be persisted.
// This function cleans the coordinate data so those outdated values don't get exported.
// See https://linear.app/hex/issue/EXP-905/map-cells-exporting-invalid-params-to-yaml-which-error-on-import
function cleanCoordinateDataForExport(
  coordinates: MapCoordinateData,
): MapCoordinateData {
  if (coordinates.system === MapCoordinateSystem.latlng) {
    return {
      system: coordinates.system,
      dataFrameColumn: coordinates.dataFrameColumn,
    };
  } else if (coordinates.system === MapCoordinateSystem.lnglat) {
    return {
      system: coordinates.system,
      dataFrameColumn: coordinates.dataFrameColumn,
    };
  } else if (coordinates.system === MapCoordinateSystem.latlng_separate) {
    return {
      system: coordinates.system,
      dataFrameLatColumn: coordinates.dataFrameLatColumn,
      dataFrameLngColumn: coordinates.dataFrameLngColumn,
    };
  } else {
    assertNever(coordinates, coordinates);
  }
}
